import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

/**
 * Optional day rate from listing publicData, when offered and usable for display/pricing UI.
 * Currency falls back to the listing weekly price currency when dayRate.currency is missing.
 *
 * @param {Object} listing
 * @returns {Money|null}
 */
export const getOfferedDayRateMoney = listing => {
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const weeklyPrice = attributes.price || null;

  const dayRateRaw = publicData.dayRate;
  const dayRateAmount = typeof dayRateRaw?.amount === 'number' ? dayRateRaw.amount : null;
  const dayRateCurrency = dayRateRaw?.currency || weeklyPrice?.currency;

  if (!publicData.offerDayRate || dayRateAmount == null || dayRateAmount <= 0 || !dayRateCurrency) {
    return null;
  }

  return new Money(dayRateAmount, dayRateCurrency);
};

/**
 * Rental listings store weekly price on attributes.price.
 * Optional day rate lives in publicData.offerDayRate + publicData.dayRate.
 * When day rate is offered, UI surfaces the day rate as "per day".
 *
 * @param {Object} listing
 * @returns {{ money: Money|null, unitType: 'day'|'week'|null }}
 */
export const getRentalDisplayPrice = listing => {
  const dayRateMoney = getOfferedDayRateMoney(listing);
  if (dayRateMoney) {
    return {
      money: dayRateMoney,
      unitType: 'day',
    };
  }

  const weeklyPrice = listing?.attributes?.price || null;
  if (weeklyPrice) {
    return {
      money: weeklyPrice,
      unitType: 'week',
    };
  }

  return { money: null, unitType: null };
};

/**
 * Whether this rental listing offers a day rate (for footnotes / copy branching).
 * Same readiness rules as getRentalDisplayPrice (amount + currency).
 */
export const listingOffersDayRate = listing => !!getOfferedDayRateMoney(listing);

/**
 * Which rate is billed for a number of paid rental days, matching server line-item math.
 *
 * @param {Object} listing
 * @param {number} chargedDays
 * @returns {{ billedAs: 'daily'|'weekly', chargedDays: number, unitMoney: Object }|null}
 */
export const getRentalChargeSummary = (listing, chargedDays) => {
  if (!chargedDays || chargedDays < 1) {
    return null;
  }

  const weeklyPrice = listing?.attributes?.price;
  if (!weeklyPrice || typeof weeklyPrice.amount !== 'number') {
    return null;
  }

  const weeklyTotalAmount = Math.ceil(chargedDays / 7) * weeklyPrice.amount;
  const dayRateMoney = getOfferedDayRateMoney(listing);

  if (dayRateMoney) {
    const dailyTotalAmount = chargedDays * dayRateMoney.amount;
    if (dailyTotalAmount < weeklyTotalAmount) {
      return { billedAs: 'daily', chargedDays, unitMoney: dayRateMoney };
    }
  }

  return { billedAs: 'weekly', chargedDays, unitMoney: weeklyPrice };
};
